import React, { Suspense, lazy } from "react";
import ReactDOM from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import "./index.css";
import "./version"; // Log version on startup
import App from "./App";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import { BrowserRouter } from "react-router-dom";

// Lazy load portal components for code splitting
const StandalonePortal = lazy(() => import("./pages/StandalonePortal"));
const MailPortal = lazy(() => import("./pages/MailPortal"));
const DynamicPortalLoader = lazy(() => import("./components/DynamicPortalLoader"));
const PortalRenderer = lazy(() => import("./components/portal-templates/PortalRenderer"));

// Loading spinner for lazy-loaded components
const PortalLoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-100">
    <div className="text-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3"></div>
      <p className="text-slate-500 text-sm">Laddar...</p>
    </div>
  </div>
);

// Base domain for wildcard subdomains
const BASE_DOMAIN = 'grindstugatan.se';

// Main portal domain (www.grindstugatan.se shows the main BRF portal)
const MAIN_PORTAL_DOMAIN = `www.${BASE_DOMAIN}`;
const MAIN_PORTAL_SLUG = 'brf-grindstugatan1'; // Default BRF for main domain

// Domain-to-organization mapping for standalone portal subdomains (legacy/manual)
// Add new entries here when setting up new medlem.* subdomains
const DOMAIN_ORG_MAP: Record<string, string> = {
  'medlem.grindstugatan.se': 'brf-grindstugatan1',
  'www.grindstugatan.se': 'brf-grindstugatan1', // Main portal
  // Add more mappings as needed:
  // 'medlem.example.se': 'brf-example',
};

// Get hostname
const hostname = window.location.hostname;

// Check if we're on the main portal domain (www.grindstugatan.se or grindstugatan.se)
const isMainPortalDomain = hostname === MAIN_PORTAL_DOMAIN || hostname === BASE_DOMAIN;

// Check if we're on a wildcard subdomain (xxx.grindstugatan.se) but not www/mail/dashboard etc
const isWildcardSubdomain = hostname.endsWith(`.${BASE_DOMAIN}`) && 
  !['www', 'mail', 'dashboard', 'api', 'app', 'admin', 'medlem'].includes(hostname.replace(`.${BASE_DOMAIN}`, '').toLowerCase());

// Check if we're on a mapped custom domain
const isMappedDomain = DOMAIN_ORG_MAP[hostname] !== undefined;

// Check if we're on a standalone portal subdomain (medlem.*)
const isMedlemDomain = hostname.startsWith('medlem.');

// Check if we're on mail portal domain (mail.*)
const isMailDomain = hostname.startsWith('mail.') || hostname === 'mail.grindstugatan.se';

// Check if we're in standalone portal mode (legacy methods)
// Method 1: Window variable (from portal.html)
// Method 2: URL contains /portal.html or query param ?standalone=true
const isLegacyStandalonePortal = 
  (window as { STANDALONE_PORTAL?: boolean }).STANDALONE_PORTAL === true ||
  window.location.pathname.includes('portal.html') ||
  new URLSearchParams(window.location.search).get('standalone') === 'true';

// Get slug from: domain mapping > window variable > URL query param
const portalSlug = DOMAIN_ORG_MAP[hostname] ||
  (window as { PORTAL_SLUG?: string }).PORTAL_SLUG || 
  new URLSearchParams(window.location.search).get('org') || 
  '';

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Failed to find the root element");

const root = ReactDOM.createRoot(rootElement);

if (isMailDomain) {
  // Mail portal
  console.log('Rendering MailPortal');
  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <Suspense fallback={<PortalLoadingFallback />}>
              <MailPortal />
            </Suspense>
            <Toaster position="top-center" richColors />
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </React.StrictMode>,
  );
} else if (isWildcardSubdomain) {
  // Wildcard subdomain (abc12def.forvaltningen.se)
  console.log('Rendering DynamicPortalLoader for wildcard subdomain:', hostname);
  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <Suspense fallback={<PortalLoadingFallback />}>
          <DynamicPortalLoader hostname={hostname} baseDomain={BASE_DOMAIN} />
        </Suspense>
        <Toaster position="top-center" richColors />
      </QueryClientProvider>
    </React.StrictMode>,
  );
} else if (isMainPortalDomain) {
  // Main portal domain (www.grindstugatan.se) - show the public BRF portal with templates
  console.log('Rendering public portal for:', MAIN_PORTAL_SLUG);
  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <Suspense fallback={<PortalLoadingFallback />}>
          <PortalRenderer slug={MAIN_PORTAL_SLUG} />
        </Suspense>
        <Toaster position="top-center" richColors />
      </QueryClientProvider>
    </React.StrictMode>,
  );
} else if (isMappedDomain) {
  // Mapped custom domain - use the slug from DOMAIN_ORG_MAP directly
  const mappedSlug = DOMAIN_ORG_MAP[hostname];
  console.log('Rendering PortalRenderer for mapped domain:', hostname, '-> slug:', mappedSlug);
  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <Suspense fallback={<PortalLoadingFallback />}>
          <PortalRenderer slug={mappedSlug} />
        </Suspense>
        <Toaster position="top-center" richColors />
      </QueryClientProvider>
    </React.StrictMode>,
  );
} else if (isMedlemDomain) {
  // medlem.* subdomain without explicit mapping - try dynamic lookup
  console.log('Rendering DynamicPortalLoader for medlem domain:', hostname);
  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <Suspense fallback={<PortalLoadingFallback />}>
          <DynamicPortalLoader hostname={hostname} baseDomain={BASE_DOMAIN} />
        </Suspense>
        <Toaster position="top-center" richColors />
      </QueryClientProvider>
    </React.StrictMode>,
  );
} else if (isLegacyStandalonePortal && portalSlug) {
  // Legacy standalone portal mode
  console.log('Rendering StandalonePortal (legacy) for:', portalSlug);
  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <Suspense fallback={<PortalLoadingFallback />}>
          <StandalonePortal slug={portalSlug} />
        </Suspense>
        <Toaster position="top-center" richColors />
      </QueryClientProvider>
    </React.StrictMode>,
  );
} else {
  // Main app
  console.log('Rendering App');
  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </React.StrictMode>,
  );
}
